import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export interface PurchaseCostCategoryRecord {
  id: string;
  name: string;
  number: string;
  createdAt: string;
  updatedAt: string;
}

export type PurchaseCostCategoryDraft = Pick<PurchaseCostCategoryRecord, "name">;

const LS_KEY = "inventory.purchaseCostCategories.v2";

type PurchaseCostCategoryApiRecord = {
  id?: number | string;
  name: string;
  number?: string | number | null;
  created_at?: string | null;
  updated_at?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UpsertPurchaseCostCategoryApiResponse = {
  message?: string;
  data?: PurchaseCostCategoryApiRecord;
};

type DeletePurchaseCostCategoryApiResponse = {
  message?: string;
};

type UpsertPurchaseCostCategoryResult = {
  record: PurchaseCostCategoryRecord;
  message: string;
};

type DeletePurchaseCostCategoryResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: PurchaseCostCategoryRecord[]): PurchaseCostCategoryRecord[] {
  const byId = new Map<ID, PurchaseCostCategoryRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): PurchaseCostCategoryRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PurchaseCostCategoryRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: PurchaseCostCategoryRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): PurchaseCostCategoryApiRecord[] {
  if (Array.isArray(body)) return body as PurchaseCostCategoryApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as PurchaseCostCategoryApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as PurchaseCostCategoryApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractCategoryFromUpsertBody(body: unknown): PurchaseCostCategoryApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as PurchaseCostCategoryApiRecord;
  }
  return null;
}

function mapApiRecord(record: PurchaseCostCategoryApiRecord): PurchaseCostCategoryRecord {
  const created = record.created_at || record.createdAt || new Date().toISOString();
  const updated = record.updated_at || record.updatedAt || created;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    number: record.number == null || record.number === "" ? "" : String(record.number),
    createdAt: created,
    updatedAt: updated,
  };
}

export function listPurchaseCostCategories(): PurchaseCostCategoryRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncPurchaseCostCategoriesFromApi() {
  try {
    const collected: PurchaseCostCategoryApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/catalog/purchase-cost-categories", {
        params: { page, per_page: PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listPurchaseCostCategories();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[PurchaseCostCategories] sync failed", error);
    return listPurchaseCostCategories();
  }
}

export async function createPurchaseCostCategory(draft: PurchaseCostCategoryDraft): Promise<UpsertPurchaseCostCategoryResult> {
  const payload = { name: draft.name.trim() };
  const response = await api.post<UpsertPurchaseCostCategoryApiResponse>("/api/v1/catalog/purchase-cost-categories", payload);
  const created = extractCategoryFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create Purchase cost category response.");
  const record = mapApiRecord(created);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  return { record, message: response.data?.message || "Purchase cost category created successfully." };
}

export async function updatePurchaseCostCategory(id: ID, draft: PurchaseCostCategoryDraft): Promise<UpsertPurchaseCostCategoryResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const payload = { name: draft.name.trim() };
  const response = await api.put<UpsertPurchaseCostCategoryApiResponse>(`/api/v1/catalog/purchase-cost-categories/${id}`, payload);
  const updated = extractCategoryFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update Purchase cost category response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);

  return { record, message: response.data?.message || "Purchase cost category updated successfully." };
}

export async function deletePurchaseCostCategory(id: ID): Promise<DeletePurchaseCostCategoryResult> {
  const response = await api.delete<DeletePurchaseCostCategoryApiResponse>(`/api/v1/catalog/purchase-cost-categories/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Purchase cost category deleted successfully." };
}

